import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const GAP = 14; // keep in sync with .pl-grid { gap }

// Where the details strip goes for the card at `index` in a grid of `cols`
// columns: after the last card of that card's visual row, with the notch
// centred over the card's column.
export function stripPlacement(index, cols, count, gap = GAP) {
  const columns = Math.max(1, cols);
  const rowEnd = Math.min(count, (Math.floor(index / columns) + 1) * columns);
  const col = index % columns;
  const notch = `calc((100% - ${(columns - 1) * gap}px) / ${columns} * ${col + 0.5} + ${col * gap}px)`;
  return { rowEnd, col, notch };
}

// Number of column tracks in a computed `grid-template-columns` value ("292px 292px 292px").
export const countTracks = template =>
  Math.max(
    1,
    String(template || '')
      .split(' ')
      .filter(Boolean).length
  );

// Card grid that inserts a full-width details strip after the visual row
// holding the expanded card, with a notch pointing back at that card.
const ExpandGrid = ({ items, expandedId, renderCard, renderDetail }) => {
  const ref = useRef(null);
  const [cols, setCols] = useState(3);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      setCols(countTracks(getComputedStyle(el).gridTemplateColumns));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const children = items.map(renderCard);
  const index = items.findIndex(item => item.id === expandedId);
  if (index >= 0) {
    const { rowEnd, notch } = stripPlacement(index, cols, items.length);
    children.splice(
      rowEnd,
      0,
      <div key="pl-expanded" className="pl-xstrip" style={{ '--notch-x': notch }}>
        {renderDetail(items[index])}
      </div>
    );
  }

  return (
    <div ref={ref} className="pl-grid">
      {children}
    </div>
  );
};

ExpandGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  expandedId: PropTypes.string,
  renderCard: PropTypes.func.isRequired,
  renderDetail: PropTypes.func.isRequired
};

export default ExpandGrid;
